import type { AuthFormOutletContext } from "@v2/features/account/common";

import * as React from "react";

import { Alert } from "@replohq/lattice/blocks/alert/Alert";
import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { readPendingWorkflow } from "@replohq/lattice/blocks/workflow-checklist/pendingWorkflow";
import { Button } from "@replohq/lattice/components/button/Button";
import { Input } from "@replohq/lattice/components/input/Input";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { cn } from "@replohq/lattice/utils/cn";
import { DURATION, EASING } from "@replohq/lattice/utils/motion";
import {
  authButtonClassName,
  authFieldClassName,
} from "@v2/features/account/authFieldStyles";
import { AuthMethodDivider } from "@v2/features/account/AuthMethodDivider";
import GoogleAuth from "@v2/features/account/GoogleAuth";
import {
  getCampaignSourceFromParams,
  hasShopifyInstallIntent,
} from "@v2/features/account/signupFunnelUtils";
import { getCampaignEventProperties } from "@v2/features/agent-beta/campaignCodes";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { useTheme } from "@v2/hooks/useTheme";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { isOsmanthusEnabled } from "replo-utils/lib/osmanthus";

import { getMarketingWorkflow } from "./marketingWorkflow";
import { TermsAndPrivacy } from "./TermsAndPrivacy";
import { useSignupForm } from "./useSignupForm";

const agentGoogleAuthClassName =
  "[&_[data-slot=button]]:border-border [&_[data-slot=button]]:bg-surface [&_[data-slot=button]]:text-default [&_[data-slot=button]]:hover:bg-hover";

const agentTermsClassName = "[&>div]:text-muted [&_a]:text-default";

const SignupForm = () => {
  const { effectiveMode } = useTheme();
  const isOsmanthus = isOsmanthusEnabled();
  const [searchParams, setSearchParams] = useSearchParams();
  const shouldReduceMotion = useReducedMotion();
  const shouldShowShopifyLoginInlineAlert =
    searchParams.get("error") === "login_in_to_associate_shopify";

  const { defaultProjectType, setDefaultProjectType } =
    useOutletContext<AuthFormOutletContext>();
  const logEvent = useLogAnalytics();
  const campaignSource = getCampaignSourceFromParams(searchParams);

  // eslint-disable-next-line replo/no-use-effect -- Fire the viewed event once when the signup screen mounts.
  React.useEffect(() => {
    logEvent(
      "auth.signup.viewed",
      getCampaignEventProperties({ source: campaignSource }),
    );
  }, [logEvent, campaignSource]);

  const isWorkflowSignup = Boolean(
    isOsmanthusEnabled() &&
      (getMarketingWorkflow(searchParams) || readPendingWorkflow()),
  );
  const isShopifyInstall = hasShopifyInstallIntent(searchParams);
  const isAgentSignup = isOsmanthus && defaultProjectType === "agent";
  let headerCopy: {
    title: React.ReactNode;
    description: React.ReactNode;
    isLeftAligned?: boolean;
  };
  if (isWorkflowSignup) {
    headerCopy = {
      title: "Create your account",
      description: null,
      isLeftAligned: true,
    };
  } else if (defaultProjectType === "agent") {
    headerCopy = {
      title: (
        <>
          Sell{" "}
          <span className="font-editorial font-light italic">Anything</span>{" "}
          with Replo
        </>
      ),
      description: (
        <>
          Switch back to Replo&apos;s classic{" "}
          <button
            type="button"
            onClick={() => setDefaultProjectType("shopify_theme_builder")}
            className={cn(
              "cursor-pointer hover:underline",
              isOsmanthus ? "text-default" : "text-primary",
            )}
          >
            Shopify Theme Builder
          </button>
        </>
      ),
      isLeftAligned: true,
    };
  } else if (isShopifyInstall) {
    // Shopify App Store installs can't switch to the Agent funnel, so no beta pitch.
    headerCopy = {
      title: "Build pages for Shopify",
      description: "Design visually. Publish to your existing theme.",
    };
  } else {
    headerCopy = {
      title: "Build directly in Shopify Themes",
      description: (
        <>
          Want to try the new version of Replo? Click here to opt-in to
          Replo&apos;s{" "}
          <button
            type="button"
            onClick={() => setDefaultProjectType("agent")}
            className="cursor-pointer text-primary hover:underline"
          >
            newest beta
          </button>
          .
        </>
      ),
      isLeftAligned: true,
    };
  }

  const {
    emailRegisterProps,
    passwordRegisterProps,
    handleSubmit,
    onSubmit,
    onGoogleSubmit,
    emailError,
    passwordError,
    isLoadingCreateAccount,
    buttonShouldBeDisabled,
    displayErrorMessage,
  } = useSignupForm({ defaultProjectType });

  const removeSearchParam = () => {
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.delete("error");
      return newParams;
    });
  };

  return (
    <form
      className="no-scrollbar"
      onSubmit={(data) => {
        if (shouldShowShopifyLoginInlineAlert) {
          removeSearchParam();
        }
        void handleSubmit(onSubmit)(data);
      }}
    >
      <div
        className={cn(
          "flex flex-col justify-center",
          isWorkflowSignup && "hidden",
          !isOsmanthus && "mb-5 min-h-[6.5rem]",
          isOsmanthus &&
            !isWorkflowSignup &&
            (isAgentSignup ? "mb-4 min-h-24" : "mb-6 min-h-[6.5rem]"),
          headerCopy.isLeftAligned ? "items-start" : "items-center text-center",
        )}
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={defaultProjectType}
            initial={shouldReduceMotion ? false : { opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: -4 }}
            transition={{
              duration: shouldReduceMotion ? 0 : DURATION.standard,
              ease: EASING.outQuint,
            }}
            className={cn(
              "flex flex-col gap-4",
              headerCopy.isLeftAligned ? "items-start" : "items-center",
            )}
          >
            {isWorkflowSignup ? (
              <h2 className="w-full text-left font-sans text-lg font-semibold text-default">
                {headerCopy.title}
              </h2>
            ) : (
              <h1
                className={cn(
                  "font-editorial text-3xl font-bold text-default",
                  headerCopy.isLeftAligned && "w-full text-left",
                )}
              >
                {headerCopy.title}
              </h1>
            )}
            {headerCopy.description && (
              <p
                className={cn(
                  "text-sm text-muted",
                  headerCopy.isLeftAligned
                    ? "w-full text-left"
                    : "text-balance",
                )}
              >
                {headerCopy.description}
              </p>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      {(displayErrorMessage || shouldShowShopifyLoginInlineAlert) && (
        <Alert
          variant={
            shouldShowShopifyLoginInlineAlert && !displayErrorMessage
              ? "warning"
              : "error"
          }
          UNSAFE_className="mb-2"
          description={
            displayErrorMessage ??
            "Please create or log in to your Replo account to continue to Replo."
          }
        />
      )}
      <div className="flex w-full flex-col gap-4">
        <LabeledControl label="Email" size="base" id="email" error={emailError}>
          <Input
            id="email"
            UNSAFE_className={
              isOsmanthus || isAgentSignup ? authFieldClassName : undefined
            }
            invalid={Boolean(emailError)}
            autoComplete={isWorkflowSignup ? "email" : "off"}
            placeholder={
              isWorkflowSignup ? "you@company.com" : "Your Work Email"
            }
            {...emailRegisterProps}
            type="email"
            size="base"
          />
        </LabeledControl>
        <LabeledControl
          label="Password"
          size="base"
          id="password"
          error={passwordError}
        >
          <Input
            id="password"
            UNSAFE_className={
              isOsmanthus || isAgentSignup ? authFieldClassName : undefined
            }
            invalid={Boolean(passwordError)}
            autoComplete={isWorkflowSignup ? "new-password" : "off"}
            placeholder={
              isWorkflowSignup ? "At least 8 characters" : "••••••••••••"
            }
            {...passwordRegisterProps}
            type="password"
            size="base"
          />
        </LabeledControl>
      </div>
      {!isWorkflowSignup && (
        <div
          className={cn(
            isAgentSignup ? "mt-8" : "mt-10",
            (isOsmanthus || isAgentSignup) && agentTermsClassName,
          )}
        >
          <TermsAndPrivacy />
        </div>
      )}
      <Button
        className="w-full"
        UNSAFE_className={cn(
          isWorkflowSignup ? "mt-4" : "mt-6",
          isOsmanthus && authButtonClassName,
          isAgentSignup &&
            !isWorkflowSignup &&
            "mt-5 cursor-pointer bg-brand-seashell text-black hover:bg-brand-seashell/90",
        )}
        variant="primary"
        size="lg"
        type="submit"
        loading={isLoadingCreateAccount}
        disabled={buttonShouldBeDisabled}
      >
        {isOsmanthus ? "Create account" : "Create Account"}
      </Button>
      {isWorkflowSignup ? (
        <div className="mt-4 flex w-full flex-col gap-4">
          <AuthMethodDivider />
          <GoogleAuth
            handleGoogleCredentials={onGoogleSubmit}
            effectiveMode={effectiveMode}
            className={agentGoogleAuthClassName}
          />
          <div className={agentTermsClassName}>
            <TermsAndPrivacy subdued />
          </div>
        </div>
      ) : (
        <>
          <div className="my-4">
            {isOsmanthus ? <AuthMethodDivider /> : <Separator />}
          </div>
          <GoogleAuth
            handleGoogleCredentials={onGoogleSubmit}
            effectiveMode={effectiveMode}
            className={isAgentSignup ? agentGoogleAuthClassName : undefined}
          />
        </>
      )}
    </form>
  );
};

export default SignupForm;
